import { BehaviorSubject, Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { ISnDrawerAppContentMargin } from '../interfaces';
import { SnDrawerRef } from '../references';

@Injectable({ providedIn: 'root' })
export class SnDrawerStore {
  private readonly _drawers: Map<string, SnDrawerRef> = new Map();
  private readonly _drawersChange$: Subject<void> = new Subject();

  private readonly _appContentMarginChange$: BehaviorSubject<ISnDrawerAppContentMargin>;

  private _drawerSideLeft: SnDrawerRef;
  private _drawerSideRight: SnDrawerRef;

  constructor() {
    this._appContentMarginChange$ = new BehaviorSubject({ left: 0, right: 0 });

    this._drawersChange$.subscribe(() => {
      this._validateDrawers();
      this._updateAppContentMargins();

      this._drawers.forEach((drawerRef) => {
        this._watchDrawerMode(drawerRef);
        this._watchDrawerWidth(drawerRef);
        this._watchDrawerPosition(drawerRef);
      });
    });
  }

  get drawers(): ReadonlyMap<string, SnDrawerRef> {
    return this._drawers;
  }

  get appContentMargins(): ISnDrawerAppContentMargin {
    return this._appContentMarginChange$.value;
  }

  get appContentMarginChange$(): Observable<ISnDrawerAppContentMargin> {
    return this._appContentMarginChange$.asObservable();
  }

  public set(id: string, ref: SnDrawerRef): void {
    this._drawers.set(id, ref);
    this._drawersChange$.next();
  }

  public delete(id: string): void {
    this._drawers.delete(id);
    this._drawersChange$.next();
  }

  private _validateDrawers(): void {
    this._drawerSideLeft = null;
    this._drawerSideRight = null;

    this._drawers.forEach((drawerRef) => {
      if (!drawerRef.isModal && drawerRef.position === 'left') {
        this._drawerSideLeft = drawerRef;
      }

      if (!drawerRef.isModal && drawerRef.position === 'right') {
        this._drawerSideRight = drawerRef;
      }
    });
  }

  private _watchDrawerPosition(drawerRef: SnDrawerRef): void {
    drawerRef.positionChange$.pipe(takeUntil(this._drawersChange$)).subscribe(() => {
      this._validateDrawers();
      this._updateAppContentMargins();
    });
  }

  private _watchDrawerMode(drawerRef: SnDrawerRef): void {
    drawerRef.modeChange$.pipe(takeUntil(this._drawersChange$)).subscribe((mode) => {
      this._validateDrawers();
      this._updateAppContentMargins();
    });
  }

  private _watchDrawerWidth(drawerRef: SnDrawerRef): void {
    drawerRef.widthChange$.pipe(takeUntil(this._drawersChange$)).subscribe(() => {
      this._updateAppContentMargins();
    });
  }

  private _updateAppContentMargins(): void {
    let left = 0;
    let right = 0;

    if (this._drawerSideLeft) {
      left += this._drawerSideLeft.width;
    }

    if (this._drawerSideRight) {
      right += this._drawerSideRight.width;
    }

    const contentMargins = this._appContentMarginChange$.value;

    if (left !== contentMargins.left || right !== contentMargins.right) {
      this._appContentMarginChange$.next({ left, right });
    }
  }
}
